// Privacy policy content.
// Sections 1-15 derive from Ekubur.Privacy Policy rev.docx; sections 15-20 are
// app-specific additions covering Google user data (required for Google OAuth
// verification) and account/data deletion. Regenerate static HTML with
// scripts/prerender-legal.ts.

export type PrivacyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'label'; text: string }
  | { kind: 'li'; text: string }

export interface PrivacySection {
  number: number
  title: string
  blocks: PrivacyBlock[]
}

export const privacyMeta = {'site': 'Ekubur.my', 'governed': 'Governed by the Laws of Malaysia', 'lastUpdated': '19 September 2026'}

export const privacySections: PrivacySection[] = [
  {
    number: 1,
    title: "Introduction",
    blocks: [
      { kind: "p", text: "This Privacy Policy explains how AI Ahad Sdn. Bhd. (Platform Owner) and B TAG VENTURE Sendirian Berhad (Company No. 201901031508 / 1340838-P) (Platform Operator) (\"Ekubur.my\", \"Akhirat\", \"we\", \"our\", or \"us\") collect, use, disclose, retain, protect, and otherwise process personal data through the Ekubur.my platform (also known as Akhirat) and related services." },
      { kind: "p", text: "This Privacy Policy should be read together with our Terms of Service at https://ekubur.my/terms-of-service." },
      { kind: "p", text: "By accessing or using Ekubur.my, you acknowledge that you have read and understood this Privacy Policy." },
    ],
  },
  {
    number: 2,
    title: "Scope",
    blocks: [
      { kind: "label", text: "This Privacy Policy applies to:" },
      { kind: "li", text: "the Ekubur.my website and platform;" },
      { kind: "li", text: "account registration and authentication;" },
      { kind: "li", text: "funeral coordination services;" },
      { kind: "li", text: "service bookings;" },
      { kind: "li", text: "customer support;" },
      { kind: "li", text: "communications, including transactional email;" },
      { kind: "li", text: "payment processing;" },
      { kind: "li", text: "mobile applications (if introduced);" },
      { kind: "li", text: "future services that reference this Policy." },
      { kind: "p", text: "This Policy does not apply to independent third-party websites, mosques, cemeteries, transport providers, payment providers, or other external organisations operating under their own policies." },
    ],
  },
  {
    number: 3,
    title: "Personal Data We Collect",
    blocks: [
      { kind: "label", text: "Depending on the services requested, we may collect:" },
      { kind: "label", text: "Identity Information" },
      { kind: "li", text: "Full name" },
      { kind: "li", text: "National Registration Identity Card (NRIC) information" },
      { kind: "li", text: "Passport information" },
      { kind: "li", text: "Date of birth" },
      { kind: "label", text: "Contact Information" },
      { kind: "li", text: "Email address" },
      { kind: "li", text: "Telephone number" },
      { kind: "li", text: "Residential address" },
      { kind: "li", text: "Emergency contact information" },
      { kind: "label", text: "Funeral Coordination Information" },
      { kind: "li", text: "Information relating to the deceased" },
      { kind: "li", text: "Name of deceased" },
      { kind: "li", text: "Date and location of death" },
      { kind: "li", text: "Burial preferences" },
      { kind: "li", text: "Cemetery details" },
      { kind: "li", text: "Funeral-service instructions" },
      { kind: "label", text: "Account Information" },
      { kind: "li", text: "Username" },
      { kind: "li", text: "Password credentials (stored only as bcrypt hashes — we do not store passwords in readable form)" },
      { kind: "li", text: "Account preferences" },
      { kind: "li", text: "Login history" },
      { kind: "li", text: "Two-factor authentication settings (encrypted)" },
      { kind: "label", text: "Payment Information" },
      { kind: "li", text: "Payment transaction references" },
      { kind: "li", text: "Billing information" },
      { kind: "li", text: "Payment status" },
      { kind: "p", text: "Ekubur.my does not intentionally store payment-card information unless expressly disclosed." },
      { kind: "label", text: "Documents You Upload" },
      { kind: "li", text: "Payment receipts" },
      { kind: "li", text: "Identity and supporting documents submitted for verification" },
      { kind: "label", text: "Technical Information" },
      { kind: "li", text: "IP address" },
      { kind: "li", text: "Device information" },
      { kind: "li", text: "Browser information" },
      { kind: "li", text: "Operating system information" },
      { kind: "li", text: "Session logs" },
      { kind: "li", text: "Usage information" },
    ],
  },
  {
    number: 4,
    title: "Information Relating to Deceased Individuals",
    blocks: [
      { kind: "p", text: "Ekubur.my processes information relating to deceased individuals solely for funeral coordination, family records, and related services such as inheritance claims." },
      { kind: "label", text: "Users submitting such information represent that they:" },
      { kind: "li", text: "are authorised to do so;" },
      { kind: "li", text: "have obtained necessary family permissions;" },
      { kind: "li", text: "are providing information accurately and lawfully." },
      { kind: "p", text: "Information concerning deceased individuals may contain personal information relating to living family members, which remains protected under applicable privacy laws." },
    ],
  },
  {
    number: 5,
    title: "How We Use Personal Data",
    blocks: [
      { kind: "label", text: "We may use personal data to:" },
      { kind: "li", text: "verify account identity;" },
      { kind: "li", text: "administer accounts;" },
      { kind: "li", text: "coordinate funeral services;" },
      { kind: "li", text: "communicate with users, including by transactional email (for example account welcome messages, password resets, payment confirmations, order confirmations, and claim status updates);" },
      { kind: "li", text: "process payments;" },
      { kind: "li", text: "facilitate bookings;" },
      { kind: "li", text: "coordinate third-party funeral providers;" },
      { kind: "li", text: "provide customer support;" },
      { kind: "li", text: "prevent fraud;" },
      { kind: "li", text: "enhance service quality;" },
      { kind: "li", text: "meet regulatory obligations;" },
      { kind: "li", text: "investigate complaints;" },
      { kind: "li", text: "maintain security." },
    ],
  },
  {
    number: 6,
    title: "Disclosure of Personal Data",
    blocks: [
      { kind: "label", text: "We may disclose information to:" },
      { kind: "label", text: "Service Providers" },
      { kind: "li", text: "Funeral coordinators" },
      { kind: "li", text: "Mosques" },
      { kind: "li", text: "Surau representatives" },
      { kind: "li", text: "Grave diggers" },
      { kind: "li", text: "Transport operators" },
      { kind: "li", text: "Cemetery administrators" },
      { kind: "label", text: "Technology Providers" },
      { kind: "li", text: "Hosting providers (including Google Cloud Platform — see Section 15)" },
      { kind: "li", text: "Email delivery providers (Google — see Section 15)" },
      { kind: "li", text: "Security providers" },
      { kind: "li", text: "Communications providers" },
      { kind: "label", text: "Payment Providers" },
      { kind: "li", text: "Banks" },
      { kind: "li", text: "Payment gateways" },
      { kind: "li", text: "Financial processors" },
      { kind: "label", text: "Authorities" },
      { kind: "p", text: "Where required by law, including courts, police, government agencies, and regulatory authorities." },
      { kind: "p", text: "We do not sell personal data to third parties. We do not share personal data for advertising or marketing purposes." },
    ],
  },
  {
    number: 7,
    title: "International Transfers",
    blocks: [
      { kind: "p", text: "Personal data may be processed outside Malaysia where cloud infrastructure or technology providers operate internationally." },
      { kind: "p", text: "Where transfers occur, we will take reasonable steps to ensure data is handled consistently with applicable Malaysian legal requirements." },
    ],
  },
  {
    number: 8,
    title: "Data Security",
    blocks: [
      { kind: "label", text: "We implement reasonable administrative, organisational, and technical measures to protect information from:" },
      { kind: "li", text: "unauthorized access;" },
      { kind: "li", text: "misuse;" },
      { kind: "li", text: "disclosure;" },
      { kind: "li", text: "modification;" },
      { kind: "li", text: "destruction." },
      { kind: "p", text: "These measures include encrypting credentials and two-factor secrets, storing passwords only as bcrypt hashes, serving the platform exclusively over HTTPS/TLS, restricting administrative access, and isolating uploaded documents in access-controlled storage." },
      { kind: "p", text: "However, no system can provide absolute security and we cannot guarantee complete protection from every cyber threat." },
    ],
  },
  {
    number: 9,
    title: "Retention",
    blocks: [
      { kind: "label", text: "Personal data will be retained only:" },
      { kind: "li", text: "for service delivery;" },
      { kind: "li", text: "for operational purposes;" },
      { kind: "li", text: "to resolve disputes;" },
      { kind: "li", text: "for legal compliance;" },
      { kind: "li", text: "for security purposes." },
      { kind: "label", text: "Specific retention periods:" },
      { kind: "li", text: "Account and profile data — for as long as the account remains active, and thereafter for the period required to satisfy statutory or regulatory retention obligations;" },
      { kind: "li", text: "Payment and transaction records — for up to seven (7) years, in line with statutory bookkeeping requirements;" },
      { kind: "li", text: "Funeral coordination and family records — for as long as needed to maintain the family's records and to service inheritance or claim matters;" },
      { kind: "li", text: "Uploaded documents (receipts, identity documents) — for as long as needed for verification, audit, and dispute resolution, after which they are deleted or anonymised;" },
      { kind: "li", text: "Login and security logs — for up to twenty-four (24) months for fraud prevention and security investigations;" },
      { kind: "li", text: "Marketing preferences — until consent is withdrawn." },
      { kind: "p", text: "When information is no longer required, it will be deleted, anonymised, or securely destroyed." },
    ],
  },
  {
    number: 10,
    title: "Your Rights",
    blocks: [
      { kind: "label", text: "Subject to applicable Malaysian law, users may request:" },
      { kind: "li", text: "access to personal data;" },
      { kind: "li", text: "correction of inaccurate information;" },
      { kind: "li", text: "withdrawal of consent;" },
      { kind: "li", text: "deletion where applicable (see Section 20, Account and Data Deletion);" },
      { kind: "li", text: "restriction of processing where applicable." },
      { kind: "label", text: "Requests may be submitted using:" },
      { kind: "p", text: "privacy@ekubur.my · support@ekubur.my · customerservice@b.tag-malaysia.com" },
    ],
  },
  {
    number: 11,
    title: "Cookies",
    blocks: [
      { kind: "label", text: "Ekubur.my may use cookies and similar technologies for:" },
      { kind: "li", text: "authentication (session tokens);" },
      { kind: "li", text: "security;" },
      { kind: "li", text: "analytics;" },
      { kind: "li", text: "preference settings (for example language);" },
      { kind: "li", text: "performance monitoring." },
      { kind: "p", text: "We do not use cookies for third-party advertising or cross-site tracking." },
      { kind: "p", text: "Users may configure browser settings to manage cookies." },
    ],
  },
  {
    number: 12,
    title: "Marketing Communications",
    blocks: [
      { kind: "label", text: "Where permitted by law, we may send:" },
      { kind: "li", text: "service announcements;" },
      { kind: "li", text: "promotional information;" },
      { kind: "li", text: "educational materials." },
      { kind: "p", text: "Users may opt out of marketing communications at any time." },
      { kind: "p", text: "Service-related communications will continue where necessary." },
    ],
  },
  {
    number: 13,
    title: "Children's Privacy",
    blocks: [
      { kind: "p", text: "The Platform is not intended for use by children acting independently." },
      { kind: "p", text: "Parents and guardians should contact us if they believe information relating to a minor has been used improperly." },
    ],
  },
  {
    number: 14,
    title: "Changes to this Policy",
    blocks: [
      { kind: "p", text: "We may revise this Privacy Policy periodically." },
      { kind: "p", text: "Updated versions will be published on the Platform and become effective on the stated effective date." },
      { kind: "p", text: "Continued use of the Platform after updates constitutes acceptance of the revised Policy." },
    ],
  },
  {
    number: 15,
    title: "Google User Data — Access and Use",
    blocks: [
      { kind: "p", text: "This section describes specifically how Ekubur.my accesses, uses, stores, and shares Google user data, in accordance with the Google API Services User Data Policy." },
      { kind: "label", text: "Email delivery through Google" },
      { kind: "p", text: "We operate our own Google account (a Google Workspace/Gmail mailbox) and connect to it using OAuth 2.0 with Google's Gmail API (scope: https://www.googleapis.com/auth/gmail.send) to send transactional emails on our behalf — for example account welcome messages, password resets, payment and order confirmations, and claim status updates. This access is limited to sending email from our own mailbox. We do not read, scan, index, or store the contents of any user's Gmail or other mailbox, and we never request access to a user's email, files, contacts, or photos." },
      { kind: "label", text: "Email addresses you provide" },
      { kind: "p", text: "When you register or transact with us, you give us an email address (which may be a Gmail or other Google account address). We use it solely to deliver service-related emails to you and to identify your account. Your email address is not used for advertising and is not sold." },
      { kind: "label", text: "Google Cloud Platform" },
      { kind: "p", text: "The Platform is hosted on Google Cloud Platform (Cloud Run, Cloud SQL, and Cloud Storage). Documents you upload — payment receipts and identity or supporting documents — are stored in access-controlled Google Cloud Storage buckets. Google acts as our infrastructure and email delivery processor." },
      { kind: "label", text: "Address autocomplete" },
      { kind: "p", text: "When entering addresses (for example a residence or cemetery location), the Platform may use the Google Maps Platform Places API. The text you type is sent to Google to return address suggestions. This feature does not identify you to Google for advertising purposes, and we do not use it to build advertising profiles." },
      { kind: "label", text: "Sign-in" },
      { kind: "p", text: "Ekubur.my does not use \"Sign in with Google\". Accounts are secured with an email address and password, with optional two-factor authentication." },
    ],
  },
  {
    number: 16,
    title: "Google User Data — Storage, Protection, and Sharing",
    blocks: [
      { kind: "p", text: "OAuth 2.0 credentials used to connect our own mailbox (client ID, client secret, and refresh token) are stored only in server-side configuration accessible to the Platform's backend. They are never included in web pages, mobile apps, or browser storage, and are never shared." },
      { kind: "p", text: "Data in transit — including emails and uploaded documents — is protected using TLS encryption." },
      { kind: "p", text: "We do not sell Google user data. We do not share Google user data with third parties except: (a) Google, as the provider of the email, hosting, storage, and address-autocomplete services described in Section 15; and (b) where required by law." },
    ],
  },
  {
    number: 17,
    title: "Google User Data — Retention and Deletion",
    blocks: [
      { kind: "p", text: "Transactional emails we send are retained for as long as the related account or transaction record is retained under our Retention schedule (Section 9), after which they are deleted or anonymised." },
      { kind: "p", text: "If the Platform disconnects its Google mailbox integration, or upon your written request relating to data we hold about you, we revoke and delete the associated OAuth credentials and delete the relevant data within a reasonable period, subject to statutory retention obligations." },
      { kind: "p", text: "You may request access, correction, or deletion of your personal data using the contact details in Section 21, or follow the process in Section 20 (Account and Data Deletion)." },
    ],
  },
  {
    number: 18,
    title: "Google User Data Policy & Limited Use Disclosure",
    blocks: [
      { kind: "p", text: "Ekubur.my's use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements." },
      { kind: "label", text: "Specifically:" },
      { kind: "li", text: "We only access Google user data necessary to provide and improve user-facing features on Ekubur.my;" },
      { kind: "li", text: "We do not transfer or sell Google user data to third parties, advertising platforms, data brokers, or information resellers;" },
      { kind: "li", text: "We do not use Google user data for serving advertisements, including retargeting, personalized, or interest-based advertising;" },
      { kind: "li", text: "We do not use Google user data (or any data derived from Google APIs) to train, calibrate, or fine-tune generalized artificial intelligence (AI) or machine learning (ML) models;" },
      { kind: "label", text: "Human access to user data is strictly restricted and only permitted when:" },
      { kind: "li", text: "(1) we have your explicit permission;" },
      { kind: "li", text: "(2) it is necessary for security purposes (such as investigating abuse);" },
      { kind: "li", text: "(3) it is required to comply with applicable law; or" },
      { kind: "li", text: "(4) the data is aggregated and anonymized for internal operations." },
    ],
  },
  {
    number: 19,
    title: "Children's and Family Data on Google Services",
    blocks: [
      { kind: "p", text: "Where funeral or family records include the email addresses of family members (for example next-of-kin or beneficiaries), those addresses are used only to contact them about the services they are involved in, and are protected in the same way as member data. We do not collect data from children under 18 for account creation." },
    ],
  },
  {
    number: 20,
    title: "Account and Data Deletion",
    blocks: [
      { kind: "p", text: "You have the right to request the complete deletion of your account and associated personal data at any time." },
      { kind: "label", text: "How to request deletion:" },
      { kind: "li", text: "Send an email to privacy@ekubur.my (or support@ekubur.my) with the subject line \"Data Deletion Request\";" },
      { kind: "li", text: "Provide your registered email address and username." },
      { kind: "p", text: "Once we have verified your identity, we will permanently remove your personal profile, transaction records, and uploaded documents within thirty (30) days, except where retention is required by Malaysian tax, bookkeeping, or other applicable law (see Section 9, Retention)." },
      { kind: "p", text: "Deletion requests are free of charge. If you are unable to email us, you may also submit a deletion request through our support channels listed in Section 21." },
    ],
  },
  {
    number: 21,
    title: "Contact Information",
    blocks: [
      { kind: "label", text: "Privacy enquiries should be directed to:" },
      { kind: "p", text: "privacy@ekubur.my · support@ekubur.my" },
      { kind: "p", text: "AI Ahad Sdn. Bhd. / B TAG VENTURE Sendirian Berhad" },
      { kind: "p", text: "Company No. 201901031508 (1340838-P)" },
      { kind: "p", text: "Menara Ilham, No. 8 Jalan Binjai, Unit 12A-03, 50450 Kuala Lumpur, Malaysia" },
      { kind: "p", text: "customerservice@b.tag-malaysia.com" },
      { kind: "p", text: "+60 3 48107 101" },
    ],
  },
]
